import React, { useCallback, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  BATCH_PDC_COLORS,
  PdcCurveChart,
  computePdcYAxis,
  normalizePeakCurve,
  scalePdcSeriesForExport,
  type PdcChartSeries,
  type PdcValueScale,
  type PeakType,
} from '../../components/charts/PdcCurveChart';
import { useChartLandscapeFullscreen, useFullscreenChartHeight } from '../../hooks/useChartLandscapeFullscreen';
import { formatApiDateCompact } from '../../utils/calendarPickerLocale';
import { downloadPdcChartPng } from '../../utils/exportChartPng';

export interface BatchPdcSource {
  workoutId: number;
  athleteName: string;
  workoutDay: string;
  extended: Record<string, unknown> | null;
  weightKg: number | null;
}

interface Props {
  sources: BatchPdcSource[];
  loading?: boolean;
  /** Same default as workout summary PDC (`MobileWorkoutPdcChart`). */
  chartHeight?: number;
}

function peakBlobsFromExtended(extended: Record<string, unknown> | null): Record<string, unknown> | null {
  if (!extended) return null;
  const peaks = extended.peaks;
  if (peaks && typeof peaks === 'object') return peaks as Record<string, unknown>;
  return extended;
}

/**
 * Multi-workout PDC overlay for batch compare.
 * Uses the same shell as workout summary (`MobileWorkoutPdcChart` + `variant="summary"`).
 */
export function BatchComparisonPdcChart({ sources, loading, chartHeight = 360 }: Props) {
  const { t, i18n } = useTranslation();
  const [peakType, setPeakType] = useState<PeakType>('pw_peak');
  const [valueScale, setValueScale] = useState<PdcValueScale>('absolute');
  const [hiddenIds, setHiddenIds] = useState<Set<string>>(() => new Set());
  const [exportingPng, setExportingPng] = useState(false);
  const chartRootRef = useRef<HTMLDivElement>(null);
  const { open: fsOpen, enter: enterFs, exit: exitFs, overlayRef } = useChartLandscapeFullscreen();
  const fsChartHeight = useFullscreenChartHeight(fsOpen, overlayRef, 108);

  const multiSeries = useMemo((): PdcChartSeries[] => {
    return sources
      .map((source, idx) => {
        const peaks = peakBlobsFromExtended(source.extended);
        const curveRaw = peaks?.[peakType] as Record<string, unknown> | undefined;
        const points = normalizePeakCurve(curveRaw, source.workoutId);
        const day = formatApiDateCompact(source.workoutDay, i18n.language);
        return {
          id: String(source.workoutId),
          label: `${source.athleteName} · ${day}`,
          color: BATCH_PDC_COLORS[idx % BATCH_PDC_COLORS.length],
          points,
          workoutId: source.workoutId,
          weightKg: source.weightKg,
        };
      })
      .filter(s => s.points.length > 0);
  }, [i18n.language, peakType, sources]);

  const hasData = multiSeries.length > 0;

  const onToggleSeries = useCallback((id: string) => {
    setHiddenIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const pdcTitle = t('compare.batch.pdcTitle', 'Peak power curve (PDC)');

  const exportPdcPng = useCallback(async () => {
    if (exportingPng || !hasData) return;
    const svg = chartRootRef.current?.querySelector('.pdc-chart__svg');
    if (!(svg instanceof SVGSVGElement)) return;

    setExportingPng(true);
    try {
      const stamp = new Date().toISOString().slice(0, 10);
      const visible = multiSeries.filter(series => !hiddenIds.has(series.id));
      const scaledSeries = visible.map(series => ({
        color: series.color,
        label: series.label,
        points: scalePdcSeriesForExport(series.points, peakType, valueScale, series.weightKg),
      }));
      const allPoints = scaledSeries.flatMap(s => s.points);
      const intervals = allPoints.map(p => p.interval);
      const { yMin, yMax } = computePdcYAxis(allPoints, true, valueScale === 'perKg');
      const unit =
        valueScale === 'perKg' && peakType === 'pw_peak'
          ? 'W/kg'
          : peakType === 'pw_peak'
            ? 'W'
            : peakType === 'hr_peak'
              ? 'bpm'
              : peakType === 'sd_peak'
                ? 'km/h'
                : 'rpm';

      await downloadPdcChartPng({
        svg,
        title: pdcTitle,
        footer: 'powered by VO2peaks',
        legend: scaledSeries.map(s => ({ label: s.label, color: s.color })),
        series: scaledSeries.map(s => ({ color: s.color, points: s.points })),
        plot: {
          chartHeight,
          minInterval: Math.min(...intervals),
          maxInterval: Math.max(...intervals),
          yMin,
          yMax,
        },
        unit,
        fileName: `vo2-batch-pdc-${stamp}.png`,
        rtl: i18n.dir() === 'rtl',
      });
    } finally {
      setExportingPng(false);
    }
  }, [chartHeight, exportingPng, hasData, hiddenIds, i18n, multiSeries, peakType, pdcTitle, valueScale]);

  const chartProps = useMemo(
    () => ({
      currentCurve: [],
      compareCurve: [],
      compareStart: '',
      compareEnd: '',
      currentLabel: '',
      peakType,
      onPeakTypeChange: setPeakType,
      onCompareStartChange: () => {},
      onCompareEndChange: () => {},
      multiSeries,
      hiddenSeriesIds: hiddenIds,
      onToggleSeries,
      valueScale,
      onValueScaleChange: setValueScale,
      hideCompareControls: true,
      variant: 'summary' as const,
      loading,
    }),
    [hiddenIds, loading, multiSeries, onToggleSeries, peakType, valueScale],
  );

  if (!sources.length) return null;

  if (!hasData && !loading) {
    return <p className="cmp-batch__muted">{t('compare.batch.pdcEmpty', 'No peak curve data for selected workouts.')}</p>;
  }

  const inlineChart = (
    <PdcCurveChart
      {...chartProps}
      chartHeight={chartHeight}
      onExpand={enterFs}
      onDownloadPng={() => void exportPdcPng()}
      downloadingPng={exportingPng}
    />
  );

  const fullscreenOverlay = fsOpen
    ? createPortal(
        <div
          ref={overlayRef}
          className="pdc-fullscreen-overlay mobile-fullscreen-panel vo2-slide-up-fullscreen"
          role="dialog"
          aria-modal="true"
          aria-label={pdcTitle}
        >
          <header className="pdc-fullscreen-overlay__header">
            <button
              type="button"
              className="pdc-fullscreen-overlay__close pdc-chart__icon-btn"
              onClick={() => void exitFs()}
              aria-label={t('pdcChart.close')}
            >
              ×
            </button>
            <span className="pdc-fullscreen-overlay__spacer" aria-hidden />
          </header>
          <p className="pdc-fullscreen-overlay__hint">{t('pdcChart.rotateHint')}</p>
          <div className="pdc-fullscreen-overlay__body ltr-island">
            <PdcCurveChart
              key={`batch-pdc-fs-${fsChartHeight}`}
              {...chartProps}
              chartHeight={fsChartHeight}
              variant="summary"
              layoutMode="fullscreen"
            />
          </div>
        </div>,
        document.body,
      )
    : null;

  return (
    <>
      <section ref={chartRootRef} className="pdc-nm-card pdc-nm-card--chart-only cmp-batch__pdc" aria-label={pdcTitle}>
        {inlineChart}
      </section>
      {fullscreenOverlay}
    </>
  );
}
